// src/services/reportsService.js
import { authService } from './authService';

export const reportsService = {

    // Consolidated method for fetching different types of flagged content
    getFlaggedContentByType: async ({
      contentType = 'all', // 'all', 'goget', 'message'
      page = 1,
      limit = 20,
      severity,
      source,
      status = 'pending',
      timeframe = '24',
      searchTerm,
      sortBy = 'createdAt',
      sortOrder = -1
    } = {}) => {
      console.log('[ReportsService] Fetching flagged content:', {
        contentType, page, limit, severity, source, status, timeframe
      });
      
      const queryParams = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        status,
        ...(severity && severity !== 'all' && { severity }),
        ...(source && source !== 'all' && { source }),
        ...(timeframe && { timeframe }),
        ...(sortBy && { sortBy }),
        ...(sortOrder && { sortOrder }),
        ...(searchTerm && { searchTerm }),
        ...(contentType !== 'all' && { contentType })
      });
  
      let endpoint = '/reports/flagged';
      if (contentType === 'goget') {
        endpoint = '/reports/flagged-gogets';
      } else if (contentType === 'message') {
        endpoint = '/reports/flagged-messages';
      }
      
      try {
        const response = await authService.authenticatedFetch(`${endpoint}?${queryParams}`);
        
        if (!response.ok) {
          const error = await response.json();
          console.error(`[ReportsService] Error fetching ${contentType} content:`, error);
          throw new Error(error.message || `Failed to fetch ${contentType} content`);
        }
  
        const data = await response.json();
        console.log(`[ReportsService] ${contentType} content fetched:`, {
          count: data.reports?.length || 0,
          total: data.total || 0
        });
        
        return {
          reports: data.reports || [],
          total: data.total || 0,
          currentPage: data.currentPage || 1,
          totalPages: data.totalPages || 1
        };
      } catch (error) {
        console.error(`[ReportsService] ${contentType} fetch error:`, error);
        throw error;
      }
    },
  
    // Unified content review method
    reviewContent: async (reportId, {
      action,
      notes = '',
      contentType = 'report' // 'report', 'goget', 'message'
    }) => {
      console.log('[ReportsService] Reviewing content:', {
        reportId, 
        action,
        contentType
      });
      
      let endpoint = `/reports/${reportId}/review`;
      if (contentType === 'message') {
        endpoint = `/reports/flagged/${reportId}/review`;
      } else if (contentType === 'goget') {
        endpoint = `/reports/flagged-gogets/${reportId}/review`;
      }
      
      try {
        const response = await authService.authenticatedFetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            action: action === 'resolved' ? 'no_action_needed' : action,
            notes 
          })
        });
  
        if (!response.ok) {
          const error = await response.json();
          console.error('[ReportsService] Review error:', error);
          throw new Error(error.message || 'Failed to review content');
        }
  
        const data = await response.json();
        console.log('[ReportsService] Review success:', {
          reportId,
          contentType,
          status: data.report?.status || 'unknown'
        });
        
        return data;
      } catch (error) {
        console.error('[ReportsService] Review error details:', error);
        throw error;
      }
    },
    
    // Unified stats method
    getStatsByContentType: async (contentType = 'all', timeframe = 24) => {
      console.log('[ReportsService] Fetching stats:', { contentType, timeframe });
      
      let endpoint = `/reports/stats?timeframe=${timeframe}`;
      if (contentType === 'message') {
        endpoint = `/reports/flagged/stats?timeframe=${timeframe}`;
      } else if (contentType === 'goget') {
        endpoint = `/reports/flagged-gogets/stats?timeframe=${timeframe}`;
      }
      
      try {
        const response = await authService.authenticatedFetch(endpoint);
        
        if (!response.ok) {
          const error = await response.json();
          console.error('[ReportsService] Stats error:', error);
          throw new Error(error.message || 'Failed to fetch statistics');
        }
  
        const data = await response.json();
        
        // Format the stats based on content type
        let formattedStats = {};
        
        if (contentType === 'message') {
          formattedStats = {
            flagged: data.flaggedMessages || 0,
            highSeverity: data.highSeverity || 0,
            resolvedToday: data.resolvedFlags || 0
          };
        } else if (contentType === 'goget') {
          formattedStats = {
            flagged: data.gogetContent?.flagged || 0,
            highSeverity: data.gogetContent?.highSeverity || 0,
            resolvedToday: data.resolvedFlags || 0,
            automated: data.gogetContent?.automated || 0,
            userReported: data.gogetContent?.userReported || 0
          };
        } else {
          formattedStats = {
            pending: data.pending || 0,
            inReview: data.inReview || 0,
            resolved: data.resolved || 0,
            highPriority: data.highPriority || 0
          };
        }
        
        console.log('[ReportsService] Stats fetched:', formattedStats);
        return formattedStats;
      } catch (error) {
        console.error('[ReportsService] Stats error details:', error);
        throw error;
      }
    },
    
    // Batch review for any content type
    batchReviewContent: async (reportIds, { 
      action, 
      notes = '', 
      contentType = 'report'
    }) => {
      console.log('[ReportsService] Batch reviewing content:', { 
        contentType,
        reportCount: reportIds.length, 
        action 
      });
      
      let endpoint = '/reports/batch/review';
      if (contentType === 'message') {
        endpoint = '/reports/flagged/batch/review';
      } else if (contentType === 'goget') {
        endpoint = '/reports/flagged-gogets/batch/review';
      }
  
      try {
        const response = await authService.authenticatedFetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ reportIds, action, notes })
        });
  
        if (!response.ok) {
          const error = await response.json();
          console.error('[ReportsService] Batch review error:', error);
          throw new Error(error.message || 'Failed to batch review content');
        }
  
        const data = await response.json();
        console.log('[ReportsService] Batch review success:', {
          succeeded: data.succeeded || 0,
          failed: data.failed || 0
        });
        
        return data;
      } catch (error) {
        console.error('[ReportsService] Batch review error details:', error);
        throw error;
      }
    },

      // Get reports (keeping this for backward compatibility)
  getReports: async (params = {}) => {
    return reportsService.getFlaggedContentByType({
      ...params,
      contentType: 'all'
    });
  },
  
  // Get flagged content (keeping for backward compatibility)
  getFlaggedContent: async (params = {}) => {
    return reportsService.getFlaggedContentByType({
      ...params,
      contentType: 'message'
    });
  },
  
  // Get flagged GoGets (keeping for backward compatibility)
  getFlaggedGoGets: async (params = {}) => {
    return reportsService.getFlaggedContentByType({
      ...params,
      contentType: 'goget'
    });
  },
  
  // Stats methods for backward compatibility
  getStats: async (timeframe = 24) => {
    return reportsService.getStatsByContentType('all', timeframe);
  },
  
  getFlaggedContentStats: async (timeframe = 24) => {
    return reportsService.getStatsByContentType('message', timeframe);
  },
  
  // Review methods for backward compatibility
  reviewReport: async (reportId, params) => {
    return reportsService.reviewContent(reportId, {
      ...params,
      contentType: 'report'
    });
  },
  
  reviewFlaggedContent: async (reportId, params) => {
    return reportsService.reviewContent(reportId, {
      ...params,
      contentType: 'message'
    });
  },

  // KEEP
  // Get single report details
  getReportDetails: async (reportId) => {
    console.log('[ReportsService - Fetch Report Details]', { reportId });

    try {
      const response = await authService.authenticatedFetch(`/reports/${reportId}`);
      
      if (!response.ok) {
        const error = await response.json();
        console.error('[ReportsService - Details Error]', error);
        throw new Error(error.message || 'Failed to fetch report details');
      }

      const data = await response.json();
      console.log('[ReportsService - Details Success]', { reportId: data._id });
      
      return data;
    } catch (error) {
      console.error('[ReportsService - Details Error Details]', error);
      throw error;
    }
  },

  // Batch review multiple reports
  batchReview: async (reportIds, { action, notes }) => {
    console.log('[ReportsService - Batch Review] Using unified method');
    return reportsService.batchReviewContent(reportIds, {
      action,
      notes,
      contentType: 'report'
    });
  },

  // KEEP
  // Dismiss a report
  dismissReport: async (reportId, notes) => {
    console.log('[ReportsService - Dismiss Report]', { reportId });

    try {
      const response = await authService.authenticatedFetch(`/reports/${reportId}/dismiss`, {
        method: 'POST',
        body: JSON.stringify({ notes })
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('[ReportsService - Dismiss Error]', error);
        throw new Error(error.message || 'Failed to dismiss report');
      }

      const data = await response.json();
      console.log('[ReportsService - Dismiss Success]', {
        reportId,
        status: data.report.status
      });
      
      return data;
    } catch (error) {
      console.error('[ReportsService - Dismiss Error Details]', error);
      throw error;
    }
  },

  // KEEP
  // Unresolve a report
  unresolveReport: async (reportId, notes = '') => {
    console.log('[ReportsService - Unresolve Report]', { reportId });
  
    try {
      const response = await authService.authenticatedFetch(`/reports/${reportId}/unresolve`, {
        method: 'POST',
        body: JSON.stringify({ notes })
      });
  
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to unresolve report');
      }
  
      return response.json();
    } catch (error) {
      console.error('[ReportsService - Unresolve Error]', error);
      throw error;
    }
  },

  // KEEP
  // Add a note to a report
  addModerationNote: async (reportId, note) => {
    console.log('[ReportsService - Add Moderation Note]', { reportId });
  
    try {
      const response = await authService.authenticatedFetch(`/reports/${reportId}/notes`, {
        method: 'POST',
        body: JSON.stringify({ note })
      });
  
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to add moderation note');
      }
  
      return response.json();
    } catch (error) {
      console.error('[ReportsService - Add Moderation Note Error]', error);
      throw error;
    }
  },

  // KEEP
  // Get Report History
  getReportHistory: async (reportId) => {
    console.log('[ReportsService - Fetch History]', { reportId });
  
    try {
      const response = await authService.authenticatedFetch(`/reports/${reportId}/history`);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch report history');
      }
  
      return response.json();
    } catch (error) {
      console.error('[ReportsService - History Error]', error);
      throw error;
    }
  },


  

  // Batch review flagged content
  batchReviewFlaggedContent: async (reportIds, { action, notes }) => {
    console.log('[ReportsService - Batch Review Flagged] Using unified method');
    return reportsService.batchReviewContent(reportIds, {
      action,
      notes,
      contentType: 'message'
    });
  },
  

  // KEEP
  // Get message context for a flagged message
  getMessageContext: async (messageId) => {
    console.log('[ReportsService - Get Message Context]', { messageId });

    try {
      const response = await authService.authenticatedFetch(`/reports/flagged/${messageId}/context`);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch message context');
      }

      const data = await response.json();
      console.log('[ReportsService - Message Context Success]', {
        messageId,
        hasContext: !!data.context
      });
      
      return data;
    } catch (error) {
      console.error('[ReportsService - Message Context Error]', error);
      throw error;
    }
  },

  

    
};
